import {
  Circle,
  Clapperboard,
  GalleryHorizontalEnd,
  Image,
  type LucideIcon,
  MoreHorizontal,
  Video,
} from "lucide-react";

import type { PieceFormat, PieceStatus } from "../types";

export const PIECE_FORMATS: PieceFormat[] = [
  "reel",
  "post",
  "carousel",
  "story",
  "video",
  "other",
];

export const PIECE_STATUSES: PieceStatus[] = [
  "preparing",
  "in_review",
  "changes_requested",
  "approved",
  "published",
];

export const FORMAT_ICONS: Record<PieceFormat, LucideIcon> = {
  reel: Clapperboard,
  post: Image,
  carousel: GalleryHorizontalEnd,
  story: Circle,
  video: Video,
  other: MoreHorizontal,
};

// Status colors from the approved design: blue = waiting for the client,
// amber = changes requested, green = approved, gray = internal work.
export const STATUS_COLORS: Record<PieceStatus, string> = {
  preparing: "#5e5e68",
  in_review: "#2f6bff",
  changes_requested: "#f5a524",
  approved: "#3dd68c",
  published: "#a1a1aa",
};

export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export const CONTENT_BUCKET = "content";

/** "2026-11" for the month containing the given date. */
export const toMonthKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

/** "2026-11-05" in local time (no UTC shift). */
export const toDateKey = (date: Date): string =>
  `${toMonthKey(date)}-${String(date.getDate()).padStart(2, "0")}`;

export const parseMonthKey = (monthKey: string | null): Date => {
  const match = monthKey?.match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, 1);
};

export const addMonths = (month: Date, delta: number): Date =>
  new Date(month.getFullYear(), month.getMonth() + delta, 1);

/** Monday-first grid of 35 or 42 days covering the month. */
export const buildMonthGrid = (
  month: Date,
): { date: Date; inMonth: boolean }[] => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
  const daysInMonth = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0,
  ).getDate();
  const cellCount = offset + daysInMonth > 35 ? 42 : 35;
  return Array.from({ length: cellCount }, (_, index) => {
    const date = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + index,
    );
    return { date, inMonth: date.getMonth() === month.getMonth() };
  });
};

export const monthRange = (month: Date): { from: string; to: string } => ({
  from: toDateKey(new Date(month.getFullYear(), month.getMonth(), 1)),
  to: toDateKey(new Date(month.getFullYear(), month.getMonth() + 1, 0)),
});
